<template>
  <div class="ai-evaluation-modal" dir="rtl">
    <!-- Loading state -->
    <div v-if="isLoading" class="loading-section">
      <div class="loading-spinner"></div>
      <p class="loading-text">מריץ הערכת AI... (עשוי לקחת 30-60 שניות)</p>
    </div>

    <!-- Error state -->
    <div v-else-if="evaluation && evaluation.status === 'error'" class="error-section">
      <p class="error-text">שגיאה בהערכה: {{ evaluation.error || 'שגיאה לא ידועה' }}</p>
      <button @click="rerun" class="button is-warning">נסה שוב</button>
    </div>

    <!-- Results -->
    <div v-else-if="evaluation && evaluation.status === 'completed'" class="results-section">
      <!-- Summarizer -->
      <div class="result-block" v-if="evaluation.summarizer">
        <div class="result-header">
          <h4>סיכום הערכה - <span :class="'summary-status-' + evaluation.summarizer.decision">{{ summaryStatus(evaluation.summarizer.decision) }}</span></h4>
        </div>
        <p class="result-text" v-html="sanitize(evaluation.summarizer.reasoning)"></p>
      </div>

      <!-- Missing Info -->
      <div class="result-block" v-if="evaluation.missing_info">
        <div class="result-header">
          <h4>צריך לשאול את המועמד</h4>
        </div>
        <p class="result-text" v-html="sanitize(evaluation.missing_info.reasoning)"></p>
      </div>

      <!-- Proposer -->
      <div class="result-block" v-if="evaluation.proposer">
        <div class="result-header">
          <h4>הצעת מועמד למשרה</h4>
        </div>
        <p class="result-text" v-html="sanitize(evaluation.proposer.reasoning)"></p>
        <div class="proposer-actions">
          <button @click="copyProposerText" class="button is-small is-info is-outlined">העתק טקסט</button>
          <button @click="useForCv" class="button is-small is-success">השתמש בטקסט לשליחת CV</button>
        </div>
      </div>

      <!-- Re-run button -->
      <div class="rerun-section">
        <button @click="rerun" class="button is-warning is-outlined">הרץ הערכה מחדש</button>
      </div>
    </div>

    <!-- No data yet -->
    <div v-else class="loading-section">
      <p>טוען...</p>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'

export default {
  name: 'AiEvaluationModal',
  props: ['payload'],
  computed: {
    offer () {
      return this.payload.data
    },
    evaluation () {
      return this.$store.getters['aiEvaluation/getEvaluation'](this.offer.id)
    },
    isLoading () {
      return this.$store.getters['aiEvaluation/isLoading'](this.offer.id)
    }
  },
  created () {
    // Only fetch if we don't already have a cached result
    if (!this.evaluation) {
      this.$store.dispatch('aiEvaluation/fetchEvaluation', { offerId: this.offer.id, vm: this })
    }
  },
  methods: {
    sanitize (text) {
      if (!text) return ''
      return text.replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/&lt;b&gt;/g, '<b>').replace(/&lt;\/b&gt;/g, '</b>')
        .replace(/&lt;i&gt;/g, '<i>').replace(/&lt;\/i&gt;/g, '</i>')
        .replace(/&lt;br\s*\/?&gt;/g, '<br>')
        .replace(/\n/g, '<br>')
    },
    summaryStatus (decision) {
      const labels = {
        green: 'עובר',
        red: 'לא עובר',
        orange: 'צריך לברר עוד מידע'
      }
      return labels[decision] || decision
    },
    rerun () {
      this.$store.dispatch('aiEvaluation/fetchEvaluation', { offerId: this.offer.id, force: true, vm: this })
    },
    copyProposerText () {
      const text = this.evaluation.proposer.reasoning
      navigator.clipboard.writeText(text).then(() => {
        this.$utils.emit('show_toast', { text: 'הטקסט הועתק' })
      }).catch(() => {
        // Fallback
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$utils.emit('show_toast', { text: 'הטקסט הועתק' })
      })
    },
    useForCv () {
      // The evaluation is already in the store — CVModalForm will read it on init
      this.$utils.emit('show_toast', { text: 'הטקסט ישמש בשליחת CV' })
      this.remove(this.payload)
    },
    ...mapMutations('modals', ['remove'])
  }
}
</script>

<style scoped>
.ai-evaluation-modal {
  padding: 1.5rem;
  direction: rtl;
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #dbdbdb;
  border-top-color: #3273dc;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  color: #666;
  font-size: 1rem;
}

.error-section {
  text-align: center;
  padding: 1.5rem;
}

.error-text {
  color: #f14668;
  margin-bottom: 1rem;
}

.results-section {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.result-block {
  background: #f9f9f9;
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid #e0e0e0;
}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.result-header h4 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #363636;
  margin: 0;
}

.summary-status-green {
  color: #48c774;
}

.summary-status-orange {
  color: #ff9f43;
}

.summary-status-red {
  color: #f14668;
}

.result-text {
  color: #4a4a4a;
  line-height: 1.6;
  white-space: pre-wrap;
  font-size: 0.95rem;
}

.proposer-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.75rem;
  justify-content: flex-start;
}

.rerun-section {
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
}
</style>
